import { Field, Form } from 'react-final-form';
import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import React, { useState } from 'react';
import { ArmyBook, CategoryBook, MagicItemBook, UnitBook } from '@Models';
import validate, { ArmyBookParams } from './army-book.validate';
import ReduxFormInput from '@Utils/ReduxFormInput';
import { defaultMessages } from '@I18n/default';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage } from 'react-intl';
import ReduxFormCheck from '@Utils/ReduxFormCheck';
import classnames from 'classnames';
import ReduxFormInputTranslation from '@Utils/ReduxFormInputTranslation';
import MagicItemComponent from '@Components/publication/army/make/magic-item/magic-item.component';
import TranslationComponent from '@Components/publication/army/make/translation/translation.component';
import SharedMagicItemComponent from '@Components/publication/army/make/shared-magic-item/shared-magic-item.component';
import UnitComponent from '@Components/publication/army/make/unit/unit.component';

export interface ArmyBookFormProps {
  army: ArmyBook;
  disabled: boolean;
  onSubmit: (values: ArmyBookParams) => void;
}

const ArmyBookMakeForm: React.FC<ArmyBookFormProps> = ({
  army,
  disabled,
  onSubmit,
}) => {
  const [currentActiveTab, setCurrentActiveTab] = useState('1');

  const toggle = (tab: string) => {
    if (currentActiveTab !== tab) setCurrentActiveTab(tab);
  };

  return (
    <>
      <div className="book">
        <h2 className="text-center">
          <FormattedMessage {...defaultMessages.publicationBookShowTitle} />
          <br />
          <em>{army.name}</em>
        </h2>
      </div>
      <Form
        initialValues={{
          name: army.name,
          prefix: army.prefix,
          version: army.version,
          logoPath: army.logoPath,
          modelRules: army.modelRules,
          magicItems: army.magicItems,
          sharedMagicItems: army.sharedMagicItems,
          armyOrganisation: army.armyOrganisation,
          armyList: army.armyList,
          loc: Object.entries(army.loc.en).map(([k, v]) => ({
            name: k,
            value: v,
          })),
        }}
        subscription={{ submitting: true, pristine: true }}
        onSubmit={onSubmit}
        validate={validate}
        mutators={{
          ...arrayMutators,
        }}
      >
        {({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit}>
            <Card className="card-border p-2">
              <CardBody>
                <FormGroup row={true}>
                  <Col xs="12" md={6} lg={4}>
                    <Field
                      name="name"
                      type="text"
                      disabled={disabled}
                      component={ReduxFormInput}
                      label={defaultMessages.publicationArmyBookFormNameLabel}
                      placeHolder={
                        defaultMessages.publicationArmyBookFormNamePlaceholder
                      }
                    />
                  </Col>
                  <Col xs="12" md={6} lg={4}>
                    <Field
                      name="prefix"
                      type="text"
                      disabled={disabled}
                      component={ReduxFormInput}
                      label={defaultMessages.publicationArmyBookFormPrefixLabel}
                      placeHolder={
                        defaultMessages.publicationArmyBookFormPrefixPlaceholder
                      }
                    />
                  </Col>
                  <Col xs="12" md={6} lg={4}>
                    <Field
                      name="version"
                      type="text"
                      disabled={disabled}
                      component={ReduxFormInput}
                      label={
                        defaultMessages.publicationArmyBookFormVersionLabel
                      }
                      placeHolder={
                        defaultMessages.publicationArmyBookFormVersionPlaceholder
                      }
                    />
                  </Col>
                  <Col xs="12" md={6} lg={4}>
                    <Field
                      name="logoPath"
                      type="text"
                      disabled={disabled}
                      component={ReduxFormInput}
                      label={
                        defaultMessages.publicationArmyBookFormLogoPathLabel
                      }
                      placeHolder={
                        defaultMessages.publicationArmyBookFormLogoPathPlaceholder
                      }
                    />
                  </Col>
                </FormGroup>
              </CardBody>
            </Card>

            <div className="mt-5">
              <Nav justified tabs>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: currentActiveTab === '1',
                    })}
                    onClick={() => {
                      toggle('1');
                    }}
                  >
                    Army Organisation
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: currentActiveTab === '2',
                    })}
                    onClick={() => {
                      toggle('2');
                    }}
                  >
                    Magic Items
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: currentActiveTab === '3',
                    })}
                    onClick={() => {
                      toggle('3');
                    }}
                  >
                    Shared Magic items
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: currentActiveTab === '4',
                    })}
                    onClick={() => {
                      toggle('4');
                    }}
                  >
                    Units
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: currentActiveTab === '5',
                    })}
                    onClick={() => {
                      toggle('5');
                    }}
                  >
                    Traductions
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={currentActiveTab}>
                <TabPane tabId="1">
                  <Card className="card-border p-2">
                    <CardBody>
                      <FormGroup row={true}>
                        <FieldArray name="armyOrganisation">
                          {({ fields }) => (
                            <>
                              {fields.map((name, index) => (
                                <Col xs={12} md={6} lg={4} key={name}>
                                  <Card className={'mb-2'}>
                                    <CardBody>
                                      <Field
                                        name={`${name}.name`}
                                        type="text"
                                        disabled={disabled}
                                        loc={form.getFieldState('loc')}
                                        component={ReduxFormInputTranslation}
                                        label={
                                          defaultMessages.publicationArmyBookFormCategoryNameLabel
                                        }
                                        placeHolder={
                                          defaultMessages.publicationArmyBookFormCategoryNamePlaceholder
                                        }
                                      />
                                      <Field
                                        name={`${name}.logo`}
                                        type="text"
                                        disabled={disabled}
                                        component={ReduxFormInput}
                                        label={
                                          defaultMessages.publicationArmyBookFormCategoryLogoLabel
                                        }
                                        placeHolder={
                                          defaultMessages.publicationArmyBookFormCategoryLogoPlaceholder
                                        }
                                      />
                                      <Field
                                        name={`${name}.value`}
                                        type="text"
                                        disabled={disabled}
                                        component={ReduxFormInput}
                                        label={
                                          defaultMessages.publicationArmyBookFormCategoryValueLabel
                                        }
                                        placeHolder={
                                          defaultMessages.publicationArmyBookFormCategoryValuePlaceholder
                                        }
                                      />
                                      <Field
                                        name={`${name}.minimum`}
                                        disabled={disabled}
                                        component={ReduxFormCheck}
                                        label={
                                          defaultMessages.publicationArmyBookFormCategoryMinimumLabel
                                        }
                                      />
                                      <Button
                                        type="button" className="btn btn-age"
                                        onClick={() => fields.remove(index)}
                                      >
                                        Remove
                                      </Button>
                                    </CardBody>
                                  </Card>
                                </Col>
                              ))}
                              <Button
                                type="button" className="btn btn-age"
                                onClick={() => fields.push({} as CategoryBook)}
                              >
                                Add
                              </Button>
                            </>
                          )}
                        </FieldArray>
                      </FormGroup>
                    </CardBody>
                  </Card>
                </TabPane>
                <TabPane tabId="2">
                  <Card className="card-border p-2">
                    <CardBody>
                      <FormGroup row={true}>
                        <FieldArray name="magicItems">
                          {({ fields }) => (
                            <>
                              {fields.map((name, index) => (
                                <MagicItemComponent name={name} index={index} fields={fields} form={form} disabled={disabled} />
                              ))}
                              <Button
                                type="button" className="btn btn-age"
                                onClick={() => fields.push({} as MagicItemBook)}
                              >
                                Add
                              </Button>
                            </>
                          )}
                        </FieldArray>
                      </FormGroup>
                    </CardBody>
                  </Card>
                </TabPane>
                <TabPane tabId="3">
                  <Card className="card-border p-2">
                    <CardBody>
                      <FormGroup row={true}>
                        <FieldArray name="sharedMagicItems">
                          {({ fields }) => (
                            <>
                              {fields.map((name, index) => (
                                <SharedMagicItemComponent name={name} index={index} fields={fields} form={form} disabled={disabled} />
                              ))}
                              <Button
                                type="button" className="btn btn-age"
                                onClick={() => fields.push({} as MagicItemBook)}
                              >
                                Add
                              </Button>
                            </>
                          )}
                        </FieldArray>
                      </FormGroup>
                    </CardBody>
                  </Card>
                </TabPane>
                <TabPane tabId="4">
                  <Card className="card-border p-2">
                    <CardBody>
                      <FormGroup row={true}>
                        <FieldArray name="armyList">
                          {({ fields }) => (
                            <>
                              {fields.map((name, index) => (
                                <UnitComponent name={name} disabled={disabled} form={form} fields={fields} index={index} />
                              ))}
                              <Button
                                type="button" className="btn btn-age"
                                onClick={() => fields.push({} as UnitBook)}
                              >
                                Add
                              </Button>
                            </>
                          )}
                        </FieldArray>
                      </FormGroup>
                    </CardBody>
                  </Card>
                </TabPane>
                <TabPane tabId="5">
                  <Card className="card-border p-2">
                    <CardBody>
                      <FormGroup row={true}>
                        <FieldArray name="loc">
                          {({ fields }) => (
                            <div>
                              {fields.map((name) => (
                                <TranslationComponent name={name} disabled={disabled} />
                              ))}
                              <Button
                                type="button" className="btn btn-age"
                                onClick={() =>
                                  fields.push({} as Record<string, string>)
                                }
                              >
                                Add
                              </Button>
                            </div>
                          )}
                        </FieldArray>
                      </FormGroup>
                    </CardBody>
                  </Card>
                </TabPane>
              </TabContent>
            </div>
          </form>
        )}
      </Form>
    </>
  );
};

export default ArmyBookMakeForm;
